import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  getDramaPerformance,
  DramaPerformanceItem,
} from "@/services/firebase/revenue";
import { getAdUnlockTotalsForRange } from "@/services/firebase/ad_unlock_stats";
import { exportToCSV } from "@/utils/csvExport";
import { Download, Eye, Film, Megaphone } from "lucide-react";
import { AdminPage } from "@/components/admin-ui/admin-page";
import { AdminPageHeader } from "@/components/admin-ui/admin-page-header";
import { AdminDataTable } from "@/components/admin-ui/admin-data-table";
import type { AdminTableColumn } from "@/components/admin-ui/admin-data-table";
import { AdminLoadingSkeleton } from "@/components/admin-ui/admin-loading-skeleton";
import { AdminErrorAlert } from "@/components/admin-ui/admin-error-alert";
import { AdminEmptyState } from "@/components/admin-ui/admin-empty-state";
import { AdminStatsCard } from "@/components/admin-ui/admin-stats-card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import FinancialDateRangePicker from "@/components/financial/FinancialDateRangePicker";
import { useFinancialDateRange } from "@/store/financialDateRange";

// ============================================================
// 表格欄位設定
// ============================================================

const columns: AdminTableColumn[] = [
  { key: "drama_title", label: "劇集", sortable: true },
  { key: "company_name", label: "所屬公司", sortable: true },
  { key: "watcher_count", label: "觀看用戶", sortable: true },
  { key: "unlock_count", label: "解鎖次數", sortable: true },
  { key: "full_drama_count", label: "全劇購買", sortable: true, hideBelow: "lg" },
  { key: "ad_unlock_count", label: "廣告解鎖", sortable: true, hideBelow: "md" },
  { key: "total_coins", label: "總金幣", sortable: true },
  { key: "paid_coins", label: "Paid", sortable: true },
  { key: "free_coins", label: "Free", sortable: true },
  { key: "unique_unlock_users", label: "付費人數", sortable: true, hideBelow: "md" },
  { key: "calculated_amount", label: "片商分潤(NTD)", sortable: true },
  { key: "platform_revenue_ntd", label: "平台收益(NTD)", sortable: true },
];

// ============================================================
// Component
// ============================================================

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

export default function DramaPerformancePage() {
  const navigate = useNavigate();
  const { start: startDate, end: endDate } = useFinancialDateRange();
  const [pageSize, setPageSize] = useState<number>(25);

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ["drama-performance", startDate, endDate],
    queryFn: () => getDramaPerformance({ startDate, endDate }),
    staleTime: 30_000,
  });

  const baseDramas = useMemo<DramaPerformanceItem[]>(
    () => data?.dramas ?? [],
    [data]
  );

  // ── 廣告解鎖數據（按月分桶、跨月時 sum 完整月份；見 disclaimer）─────────
  const dramaIds = useMemo(
    () => baseDramas.map((d) => d.drama_id).filter(Boolean),
    [baseDramas]
  );
  const { data: adUnlockData } = useQuery({
    queryKey: ["drama-performance-ad-unlock", startDate, endDate, dramaIds],
    queryFn: () => getAdUnlockTotalsForRange(dramaIds, startDate, endDate),
    enabled: dramaIds.length > 0,
    staleTime: 30_000,
  });
  const adUnlockTotals = adUnlockData?.totals ?? {};
  const adUnlockPeriods = adUnlockData?.periods ?? [];

  // 把廣告解鎖次數 join 進 dramas（caller 用 ad_unlock_count 欄位讀）
  const dramas = useMemo(
    () =>
      baseDramas.map((d) => ({
        ...d,
        ad_unlock_count: adUnlockTotals[d.drama_id] ?? 0,
      })),
    [baseDramas, adUnlockTotals]
  );

  const totals = useMemo(() => {
    if (!dramas.length) return null;
    return dramas.reduce(
      (acc, d) => ({
        watcher_count: acc.watcher_count + d.watcher_count,
        unlock_count: acc.unlock_count + d.unlock_count,
        full_drama_count: acc.full_drama_count + d.full_drama_count,
        ad_unlock_count: acc.ad_unlock_count + d.ad_unlock_count,
        paid_coins: acc.paid_coins + d.paid_coins,
        free_coins: acc.free_coins + d.free_coins,
        total_coins: acc.total_coins + d.total_coins,
        calculated_amount: acc.calculated_amount + d.calculated_amount,
        platform_revenue_ntd: acc.platform_revenue_ntd + d.platform_revenue_ntd,
      }),
      {
        watcher_count: 0,
        unlock_count: 0,
        full_drama_count: 0,
        ad_unlock_count: 0,
        paid_coins: 0,
        free_coins: 0,
        total_coins: 0,
        calculated_amount: 0,
        platform_revenue_ntd: 0,
      }
    );
  }, [dramas]);

  const handleExport = () => {
    if (!dramas.length) return;
    const rows = dramas.map((d) => ({
      劇集: d.drama_title,
      劇集ID: d.drama_id,
      所屬公司: d.company_name || "(未分派)",
      觀看用戶: d.watcher_count,
      解鎖次數: d.unlock_count,
      全劇購買: d.full_drama_count,
      廣告解鎖: d.ad_unlock_count,
      總金幣: d.total_coins,
      Paid: d.paid_coins,
      Free: d.free_coins,
      付費人數: d.unique_unlock_users,
      Paid費率: d.paid_coin_rate_applied,
      Free費率: d.free_coin_rate_applied,
      片商分潤NTD: d.calculated_amount,
      平台收益NTD: d.platform_revenue_ntd,
      生命週期播放: d.drama_play_times_lifetime,
    }));
    exportToCSV(
      rows,
      [
        { key: "劇集" as const, label: "劇集" },
        { key: "劇集ID" as const, label: "劇集 ID" },
        { key: "所屬公司" as const, label: "所屬公司" },
        { key: "觀看用戶" as const, label: "觀看用戶" },
        { key: "解鎖次數" as const, label: "解鎖次數" },
        { key: "全劇購買" as const, label: "全劇購買" },
        { key: "廣告解鎖" as const, label: `廣告解鎖（${adUnlockPeriods.join(",") || "整月"}）` },
        { key: "總金幣" as const, label: "總金幣" },
        { key: "Paid" as const, label: "Paid" },
        { key: "Free" as const, label: "Free" },
        { key: "付費人數" as const, label: "付費人數" },
        { key: "Paid費率" as const, label: "Paid 費率 (NTD/coin)" },
        { key: "Free費率" as const, label: "Free 費率 (NTD/coin)" },
        { key: "片商分潤NTD" as const, label: "片商分潤 (NTD)" },
        { key: "平台收益NTD" as const, label: "平台收益 (NTD)" },
        { key: "生命週期播放" as const, label: "生命週期播放" },
      ],
      `drama_performance_${startDate}_${endDate}`
    );
  };

  return (
    <AdminPage>
      <AdminPageHeader
        title="劇集表現"
        subtitle="指定期間每部劇的觀看、解鎖、金幣消費與試算分潤"
        loading={isLoading}
        onRefresh={() => refetch()}
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            disabled={!dramas.length}
          >
            <Download className="h-4 w-4 mr-1" />
            CSV 匯出
          </Button>
        }
      />

      {/* 日期選擇 + 每頁筆數 */}
      <div className="flex flex-wrap items-end gap-3 mb-6">
        <FinancialDateRangePicker mode="range" />
        <div className="flex items-end gap-2 ml-auto">
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">
              每頁筆數
            </label>
            <Select
              value={String(pageSize)}
              onValueChange={(v) => setPageSize(Number(v))}
            >
              <SelectTrigger className="w-24">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAGE_SIZE_OPTIONS.map((n) => (
                  <SelectItem key={n} value={String(n)}>
                    {n}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {error && <AdminErrorAlert message={`載入失敗: ${(error as Error).message}`} />}

      {(isLoading || isFetching) && !data ? (
        <AdminLoadingSkeleton variant="mixed" />
      ) : !data ? null : (
        <div className="space-y-6">
          {/* 總覽卡片 */}
          {totals && (
            <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
              <AdminStatsCard
                icon={<Film className="h-4 w-4" />}
                label="顯示劇集數"
                value={dramas.length.toLocaleString()}
              />
              <AdminStatsCard
                icon={<Eye className="h-4 w-4" />}
                label="觀看用戶總人次"
                value={totals.watcher_count.toLocaleString()}
              />
              <AdminStatsCard
                icon={<Film className="h-4 w-4" />}
                label="總解鎖次數"
                value={(totals.unlock_count + totals.full_drama_count).toLocaleString()}
              />
              <AdminStatsCard
                icon={<Megaphone className="h-4 w-4" />}
                label={
                  adUnlockPeriods.length > 1
                    ? `廣告解鎖合計（${adUnlockPeriods.length} 個月）`
                    : "廣告解鎖合計"
                }
                value={totals.ad_unlock_count.toLocaleString()}
              />
              <AdminStatsCard
                icon={<Film className="h-4 w-4" />}
                label="片商分潤合計"
                value={`NTD ${totals.calculated_amount.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}`}
              />
              <AdminStatsCard
                icon={<Film className="h-4 w-4" />}
                label="平台收益合計"
                value={`NTD ${totals.platform_revenue_ntd.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}`}
              />
            </div>
          )}

          {/* 表格 */}
          {dramas.length > 0 ? (
            <AdminDataTable
              columns={columns}
              data={dramas}
              pageSize={pageSize}
              defaultSort={{ key: "total_coins", dir: "desc" }}
              getRowId={(item) => item.drama_id}
              onRowClick={(item) =>
                navigate(
                  `/financial/revenue/drama/${item.drama_id}?start=${startDate}&end=${endDate}`
                )
              }
              renderCell={(item: DramaPerformanceItem & { ad_unlock_count: number }, col) => {
                switch (col.key) {
                  case "drama_title":
                    return (
                      <span className="font-medium text-foreground">
                        {item.drama_title}
                      </span>
                    );
                  case "company_name":
                    return item.company_name ? (
                      <span className="text-sm">{item.company_name}</span>
                    ) : (
                      <span className="text-xs text-muted-foreground/60">(未分派)</span>
                    );
                  case "watcher_count":
                    return (
                      <span className="text-right block tabular-nums">
                        {item.watcher_count.toLocaleString()}
                      </span>
                    );
                  case "unlock_count":
                    return (
                      <span className="text-right block tabular-nums">
                        {item.unlock_count.toLocaleString()}
                      </span>
                    );
                  case "full_drama_count":
                    return (
                      <span className="text-right block tabular-nums text-muted-foreground">
                        {item.full_drama_count.toLocaleString()}
                      </span>
                    );
                  case "ad_unlock_count":
                    return (
                      <span
                        className={`text-right block tabular-nums ${
                          item.ad_unlock_count > 0
                            ? "text-amber-700 dark:text-amber-400 font-medium"
                            : "text-muted-foreground/50"
                        }`}
                      >
                        {item.ad_unlock_count.toLocaleString()}
                      </span>
                    );
                  case "total_coins":
                    return (
                      <span className="text-right block tabular-nums font-medium">
                        {item.total_coins.toLocaleString()}
                      </span>
                    );
                  case "paid_coins":
                    return (
                      <span className="text-right block tabular-nums text-emerald-700 dark:text-emerald-400">
                        {item.paid_coins.toLocaleString()}
                      </span>
                    );
                  case "free_coins":
                    return (
                      <span className="text-right block tabular-nums text-sky-700 dark:text-sky-400">
                        {item.free_coins.toLocaleString()}
                      </span>
                    );
                  case "unique_unlock_users":
                    return (
                      <span className="text-right block tabular-nums text-muted-foreground">
                        {item.unique_unlock_users.toLocaleString()}
                      </span>
                    );
                  case "calculated_amount":
                    return (
                      <span className="text-right block tabular-nums font-medium text-amber-700 dark:text-amber-400">
                        NTD {item.calculated_amount.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </span>
                    );
                  case "platform_revenue_ntd":
                    return (
                      <span
                        className={`text-right block tabular-nums font-medium ${
                          item.platform_revenue_ntd >= 0
                            ? "text-emerald-700 dark:text-emerald-400"
                            : "text-rose-600 dark:text-rose-400"
                        }`}
                      >
                        NTD {item.platform_revenue_ntd.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </span>
                    );
                  default:
                    return null;
                }
              }}
            />
          ) : (
            <AdminEmptyState title="此期間暫無劇集表現資料" />
          )}

          {/* 底部說明 */}
          <div className="text-xs text-muted-foreground space-y-1 border-t pt-4">
            <p>
              <strong>觀看用戶：</strong>期間內有觀看紀錄更新（<code>watched_at</code>）的不重複用戶數
            </p>
            <p>
              <strong>解鎖次數：</strong>期間內 <code>transactions.type = unlock</code> 的筆數
            </p>
            <p>
              <strong>全劇購買：</strong>期間內 <code>type = full_drama_coin_purchase</code> 的筆數
            </p>
            <p>
              <strong>廣告解鎖：</strong>看廣告解鎖下一集的次數，按月（Asia/Taipei）分桶。
              當查詢範圍跨月時，數字為涵蓋的<strong>完整月份加總</strong>
              {adUnlockPeriods.length > 0 && (
                <>（本次包含：<code>{adUnlockPeriods.join(", ")}</code>）</>
              )}
              。本欄未納入分潤公式（Phase 3 接 AdMob 報表後才會計入）
            </p>
            <p>
              <strong>片商分潤：</strong>paid × 當下費率 + free × 本月公告費率（僅參考，實際結算以合約為準）
            </p>
            <p>
              <strong>平台收益：</strong>該劇 paid_coins × 期間內 IAP 淨值/枚 − 片商分潤。其中 IAP 淨值/枚 = (期間 IAP gross − Apple/Google 抽成) / 期間 paid coin 銷售總量。free coin 不貢獻平台 IAP 收入但 payout 仍由平台承擔。
            </p>
            <p>
              <strong>生命週期播放：</strong>
              drama 文件上累積的播放計數（CSV 匯出才顯示，不隨日期範圍變動）
            </p>
          </div>
        </div>
      )}
    </AdminPage>
  );
}
